import { TPSScores } from '../types/tps.types';
import {
  signedDifferentialScore,
  softmaxRankings,
  inferredConfidence,
  userTraitMean,
  type DifferentialTraitSet,
} from './utils';

export interface LoveLanguageDetail {
  primary: string;
  secondary: string;
  rankings: Array<{ language: string; score: number; percentage: number }>;
  confidence: number;
  confidenceLabel: 'Low' | 'Medium' | 'High';
  mixed: boolean;
  noSignal: boolean;
  description: string;
}

/**
 * Trait sets fitted against the type-simulated love-language personas
 * (15 rows: 5 targets x 3 replicates). Positives are semantically anchored;
 * remaining slots were selected by coordinate ascent on persona accuracy
 * with a capped-margin objective. Fitted set scores 15/15 on that corpus
 * (previous hand-authored set scored 5/15).
 *
 * Two structural notes:
 *  - The TPS item bank contains no "touch" content, so Physical Touch is
 *    identified indirectly (Self-Indulgent + Dynamic + Social, against
 *    Stoic / Self-Mastery restraint) rather than by a touch trait.
 *  - Receiving Gifts is NOT keyed on Extrinsic: the gift-primary personas
 *    score low on Extrinsic and high on Lawful/Structured symbolic ritual.
 *    Extrinsic is the Words of Affirmation discriminator.
 */
export const LOVE_LANGUAGE_TRAITS: Record<string, DifferentialTraitSet & { description: string }> = {
  'Quality Time': {
    positive: ['Communal Navigate', 'Assertive', 'Intrinsic'],
    positiveModerate: ['Structured', 'Direct', 'Universal'],
    negative: ['Modular', 'Diplomatic', 'Optimistic'],
    signatureTrait: 'Communal Navigate',
    description: 'Giving undivided attention and being fully present',
  },
  'Physical Touch': {
    positive: ['Self-Indulgent', 'Dynamic', 'Social'], // TPS-EXEMPT: Social=processing.reality.social
    positiveModerate: ['Physical', 'Intrinsic', 'Passive'],
    negative: ['Stoic', 'Self-Mastery', 'Extrinsic'],
    signatureTrait: 'Self-Indulgent',
    description: 'Physical expressions of love and affection',
  },
  'Words of Affirmation': {
    positive: ['Extrinsic', 'Turbulent', 'Self-Aware'],
    positiveModerate: ['Direct', 'Social', 'Universal'], // TPS-EXEMPT: Social=processing.reality.social
    negative: ['Intrinsic', 'Stoic', 'Physical'],
    signatureTrait: 'Extrinsic',
    description: 'Verbal compliments, encouragement, and appreciation',
  },
  'Acts of Service': {
    positive: ['Pragmatic', 'Analytical', 'Physical'],
    positiveModerate: ['Direct', 'Realistic', 'Self-Mastery'],
    negative: ['Extrinsic', 'Social', 'Turbulent'], // TPS-EXEMPT: Social=processing.reality.social
    signatureTrait: 'Pragmatic',
    description: 'Doing helpful things for the people you love',
  },
  'Receiving Gifts': {
    positive: ['Lawful', 'Structured', 'Optimistic'],
    positiveModerate: ['Analytical', 'Passive', 'Static'],
    negative: ['Pragmatic', 'Realistic', 'Intuitive'],
    signatureTrait: 'Lawful',
    description: 'Thoughtful gifts as symbols of love and affection',
  },
};

export function calculateLoveLanguages(scores: TPSScores): LoveLanguageDetail {
  const mean = userTraitMean(scores);

  const raw = Object.entries(LOVE_LANGUAGE_TRAITS).map(([lang, traits]) => ({
    key: lang,
    score: signedDifferentialScore(scores, traits, mean),
    tiebreak: traits.signatureTrait ? (scores[traits.signatureTrait] ?? mean) - mean : 0,
  }));

  const ranked = softmaxRankings(raw);
  const { confidence, confidenceLabel, mixed, noSignal } = inferredConfidence(ranked);

  const rankings = ranked.map((r) => ({
    language: r.key,
    score: r.rawScore,
    percentage: r.percentage,
  }));

  const primary = rankings[0].language;
  const secondary = rankings[1]?.language ?? primary;
  const primaryConfig = LOVE_LANGUAGE_TRAITS[primary];

  return {
    primary,
    secondary,
    rankings,
    confidence,
    confidenceLabel,
    mixed,
    noSignal,
    description: primaryConfig.description,
  };
}

// Love language icons (emoji)
export const LOVE_LANGUAGE_ICONS: Record<string, string> = {
  'Quality Time': '⏰',
  'Physical Touch': '🤗',
  'Words of Affirmation': '💬',
  'Acts of Service': '🛠️',
  'Receiving Gifts': '🎁',
};

export const LOVE_LANGUAGE_COLORS: Record<string, string> = {
  'Quality Time': 'hsl(210, 70%, 50%)',
  'Physical Touch': 'hsl(350, 75%, 55%)',
  'Words of Affirmation': 'hsl(45, 85%, 50%)',
  'Acts of Service': 'hsl(150, 60%, 40%)',
  'Receiving Gifts': 'hsl(280, 65%, 55%)',
};
